import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LoginForm from '../../components/Auth/LoginForm';
import SignupForm from '../../components/Auth/SignupForm';

// Self-signup is disabled for now (students are onboarded via school SSO).
// Set to true to re-enable the Sign Up tab.
const SIGNUP_ENABLED = false;

// Sample article cards for different age groups
const sampleArticles = [
  {
    ageGroup: '6-9 years',
    title: 'Amazing Discovery: Scientists Find New Dinosaur!',
    category: 'Science',
    categoryColor: 'bg-purple-500',
    excerpt: 'Scientists discovered a new dinosaur fossil! It was as big as a school bus and had a long neck...',
    image: '🦕'
  },
  {
    ageGroup: '10-13 years',
    title: 'Mars Rover Discovers Ancient River Evidence',
    category: 'Technology',
    categoryColor: 'bg-blue-500',
    excerpt: 'NASA\'s Mars rover found evidence of an ancient river system that existed billions of years ago, suggesting Mars once had flowing water...',
    image: '🚀'
  },
  {
    ageGroup: '14+ years',
    title: 'Climate Summit: World Leaders Pledge Carbon Neutrality',
    category: 'Environment',
    categoryColor: 'bg-green-500',
    excerpt: 'At the annual climate summit, representatives from 150 nations committed to achieving carbon neutrality by 2050 through renewable energy initiatives...',
    image: '🌍'
  }
];

const LandingPage: React.FC = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(0);

  // Rotate through sample articles
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentArticle((prev) => (prev + 1) % sampleArticles.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex">
      {/* Left Pane - Branding (45%) */}
      <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 p-12 flex-col justify-center text-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -top-20 -left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-300/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, -50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <div className="bg-white p-4 rounded-3xl shadow-2xl">
                <img
                  src="/knoozy-logo.png"
                  alt="Knoozy Logo"
                  className="h-24 w-24 object-contain"
                />
              </div>
            </div>

            {/* Heading */}
            <h1 className="text-5xl font-bold mb-3 tracking-tight">
              Welcome to Knoozy
            </h1>
            <p className="text-xl mb-8 text-purple-100 font-medium">
              Current Affairs Made Easy for GenZ
            </p>

            {/* Features Grid - 2x2 */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex flex-col items-center justify-center bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center"
              >
                <div className="text-3xl mb-2">📚</div>
                <h3 className="text-sm font-bold">Age-Appropriate Content</h3>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex flex-col items-center justify-center bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center"
              >
                <div className="text-3xl mb-2">🌍</div>
                <h3 className="text-sm font-bold">Daily Current Affairs</h3>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex flex-col items-center justify-center bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center"
              >
                <div className="text-3xl mb-2">🎨</div>
                <h3 className="text-sm font-bold">Fun & Engaging</h3>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex flex-col items-center justify-center bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center"
              >
                <div className="text-3xl mb-2">✅</div>
                <h3 className="text-sm font-bold">Safe & Educational</h3>
              </motion.div>
            </div>

            {/* Sample Article Preview Section */}
            <div className="mt-8">
              <p className="text-sm text-purple-200 mb-3 text-center font-medium">
                See how we adapt content for different ages
              </p>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentArticle}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white/15 backdrop-blur-md rounded-2xl p-5 border border-white/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`${sampleArticles[currentArticle].categoryColor} text-white text-xs px-3 py-1 rounded-full font-semibold`}>
                      {sampleArticles[currentArticle].category}
                    </span>
                    <span className="text-purple-200 text-xs font-medium">
                      {sampleArticles[currentArticle].ageGroup}
                    </span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-4xl">{sampleArticles[currentArticle].image}</div>
                    <div className="flex-1">
                      <h4 className="font-bold text-white text-sm mb-1.5 leading-tight">
                        {sampleArticles[currentArticle].title}
                      </h4>
                      <p className="text-purple-100 text-xs leading-relaxed">
                        {sampleArticles[currentArticle].excerpt}
                      </p>
                    </div>
                  </div>

                  {/* Progress Dots */}
                  <div className="flex justify-center space-x-2 mt-4">
                    {sampleArticles.map((_, idx) => (
                      <div
                        key={idx}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          idx === currentArticle ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Pane - Auth Forms (55%) */}
      <div className="w-full lg:w-[55%] flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <div className="bg-white p-3 rounded-2xl shadow-xl">
              <img
                src="/knoozy-logo.png"
                alt="Knoozy Logo"
                className="h-16 w-16 object-contain"
              />
            </div>
          </div>

          {/* Mobile Heading */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Knoozy</h1>
            <p className="text-gray-600 font-medium">Current Affairs Made Easy for GenZ</p>
          </div>

          {/* Form Container */}
          <motion.div
            className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            {/* Toggle Tabs (hidden while self-signup is disabled) */}
            {SIGNUP_ENABLED && (
              <div className="flex mb-8 bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setIsSignup(false)}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    !isSignup
                      ? 'bg-white text-indigo-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Login
                </button>
                <button
                  onClick={() => setIsSignup(true)}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    isSignup
                      ? 'bg-white text-indigo-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Forms with Animation */}
            <AnimatePresence mode="wait">
              {(!SIGNUP_ENABLED || !isSignup) ? (
                <motion.div
                  key="login"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <LoginForm />
                </motion.div>
              ) : (
                <motion.div
                  key="signup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <SignupForm />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Footer */}
          <p className="text-center text-gray-500 text-sm mt-6">
            By continuing, you agree to Knoozy's Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
